import { useState } from 'react';
import { Trash2, X, ChevronLeft, ChevronRight, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_BASE_URL } from '../api/config';

export const TABLE_URL = `${API_BASE_URL}/table.php`;

/* ── Field definitions ─────────────────────────────────────────────── */
export const SECTIONS = [
  {
    id: 'personal', label: 'Personal',
    fields: [
      { key: 'client_name',    label: 'Client Name',      required: true },
      { key: 'father_name',    label: 'Father Name' },
      { key: 'mother_name',    label: 'Mother Name' },
      { key: 'date_of_birth',  label: 'Date of Birth',    type: 'date' },
      { key: 'email',          label: 'Email',             type: 'email' },
      { key: 'contact_no',     label: 'Contact No' },
      { key: 'marital_status', label: 'Marital Status',   type: 'select', options: ['Single', 'Married', 'Divorced', 'Widowed'] },
      { key: 'dependents',     label: 'Dependents',        type: 'number' },
      { key: 'spouse_name',    label: 'Spouse Name' },
      { key: 'spouse_dob',     label: 'Spouse DOB',        type: 'date' },
    ],
  },
  {
    id: 'identity', label: 'Identity',
    fields: [
      { key: 'pan_no',    label: 'PAN No' },
      { key: 'aadhar_no', label: 'Aadhar No' },
    ],
  },
  {
    id: 'address', label: 'Address',
    fields: [
      { key: 'address_door_no',      label: 'Door No' },
      { key: 'current_address',      label: 'Current Address',      type: 'textarea' },
      { key: 'current_landmark',     label: 'Current Landmark' },
      { key: 'residential_address',  label: 'Residential Address',  type: 'textarea' },
      { key: 'residential_landmark', label: 'Residential Landmark' },
      { key: 'residence_ownership',  label: 'Residence Ownership',  type: 'select', options: ['OWN', 'RENTED'] },
      { key: 'years_in_residence',   label: 'Years in Residence',   type: 'number' },
      { key: 'staying_in_chennai',   label: 'Staying in Chennai',   type: 'select', options: ['Yes', 'No'] },
      { key: 'permanent_address',    label: 'Permanent Address',    type: 'textarea' },
      { key: 'permanent_landmark',   label: 'Permanent Landmark' },
    ],
  },
  {
    id: 'employment', label: 'Employment',
    fields: [
      { key: 'customer_type',           label: 'Customer Type',      type: 'select', options: ['SALARIED', 'SELF_EMPLOYED'] },
      { key: 'company_name',            label: 'Company Name' },
      { key: 'office_name',             label: 'Office Name' },
      { key: 'office_address',          label: 'Office Address',     type: 'textarea' },
      { key: 'office_landmark',         label: 'Office Landmark' },
      { key: 'designation',             label: 'Designation' },
      { key: 'official_mail',           label: 'Official Email',     type: 'email' },
      { key: 'office_landline',         label: 'Office Landline' },
      { key: 'current_job_experience',  label: 'Current Job Exp.' },
      { key: 'total_work_experience',   label: 'Total Work Exp.' },
      { key: 'education_qualification', label: 'Education' },
      { key: 'nature_of_business',      label: 'Nature of Business' },
    ],
  },
  {
    id: 'financial', label: 'Financial',
    fields: [
      { key: 'gross_income',                label: 'Gross Income',        type: 'number' },
      { key: 'net_income',                  label: 'Net Income',          type: 'number' },
      { key: 'required_loan_amount',        label: 'Loan Amount',         type: 'number' },
      { key: 'tenure',                      label: 'Tenure' },
      { key: 'bank_name',                   label: 'Bank Name' },
      { key: 'live_loan',                   label: 'Live Loan',           type: 'textarea' },
      { key: 'current_business_experience', label: 'Business Experience' },
      { key: 'dsa_name',                    label: 'DSA Name' },
      { key: 'product_type',                label: 'Product Type' },
      { key: 'loan_od',                     label: 'Loan OD',             type: 'select', options: ['Secured', 'Unsecured'] },
      { key: 'status',                      label: 'Status',              type: 'select', options: ['Select Status', 'Hold', 'Disbursed', 'Approved', 'Rejected', 'WIP-Login', 'Pending'] },
      { key: 'status_description',          label: 'Status Description' },
    ],
  },
];

export const ALL_FIELDS = SECTIONS.flatMap(s => s.fields);
export const emptyForm = () => Object.fromEntries(ALL_FIELDS.map(f => [f.key, f.key === 'status' ? 'Select Status' : '']));

/* ── Toast ─────────────────────────────────────────────────────────── */
export const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
    ${type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
    {type === 'success'
      ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
      : <AlertCircle size={16} className="text-red-500 shrink-0" />}
    {message}
    <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
  </div>
);

/* ── Description modal ─────────────────────────────────────────────── */
export const DescriptionModal = ({ record, onClose, onSaved }) => {
  const [desc, setDesc] = useState(record?.status_description || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (desc === (record?.status_description || '')) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const id = record?.application_id || record?.id || record?.applicant_id;
      const res = await fetch(`${API_BASE_URL}/applications.php?action=update_status_description&id=${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status_description: desc })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update description');
      onSaved(id, desc);
      onClose();
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Update Status Description</h3>
        <p className="text-sm text-gray-500 mb-4">Add additional notes or description for this application's current status.</p>
        
        <textarea
          rows={4}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Enter status description..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none mb-4"
          autoFocus
        />
        
        <div className="flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save Description'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Delete confirm modal ──────────────────────────────────────────── */
export const DeleteModal = ({ record, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
          <Trash2 size={18} className="text-red-600" />
        </div>
        <h3 className="font-bold text-gray-900">Delete Record</h3>
      </div>
      <p className="text-sm text-gray-600 mb-5">
        Are you sure you want to delete{' '}
        <span className="font-semibold text-gray-900">
          #{record.application_id} — {record.client_name}
        </span>? This cannot be undone.
      </p>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60">
          {loading && <Loader2 size={14} className="animate-spin" />}
          Delete
        </button>
      </div>
    </div>
  </div>
);

/* ── View (read-only) Modal ────────────────────────────────────────── */
export const ViewModal = ({ record, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState(0);
  const currentSection = SECTIONS[activeTab];

  const formatVal = (field, val) => {
    if (!val && val !== 0) return <span className="text-gray-400 italic">—</span>;
    if (field.type === 'number' && field.key.includes('income' || 'amount')) {
      return `₹ ${Number(val).toLocaleString('en-IN')}`;
    }
    if (field.type === 'date') {
      return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return val;
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              Record Details — #{record.application_id}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{record.client_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {onEdit && (
              <button onClick={onEdit}
                className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors">
                Edit Record
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 gap-1 overflow-x-auto">
          {SECTIONS.map((s, i) => (
            <button key={s.id} onClick={() => setActiveTab(i)}
              className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors
                ${activeTab === i
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Fields */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {currentSection.fields.map(field => (
              <div key={field.key} className="bg-gray-50 rounded-lg px-4 py-3">
                <p className="text-xs text-gray-400 mb-0.5">{field.label}</p>
                <p className="text-sm font-medium text-gray-800">
                  {formatVal(field, record[field.key])}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{activeTab + 1} / {SECTIONS.length}</span>
            <span className="font-medium text-gray-600">{currentSection.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setActiveTab(prev => Math.max(0, prev - 1))} disabled={activeTab === 0}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1">
              <ChevronLeft size={13} /> Prev
            </button>
            <button onClick={() => setActiveTab(prev => Math.min(SECTIONS.length - 1, prev + 1))} disabled={activeTab === SECTIONS.length - 1}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1">
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Form Modal (Create / Edit) ────────────────────────────────────── */
export const FormModal = ({ record, onClose, onSaved, sourceOverride }) => {
  const isEdit = !!record?.id;
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(() => {
    if (isEdit) {
      const f = emptyForm();
      ALL_FIELDS.forEach(({ key }) => { f[key] = record[key] ?? ''; });
      return f;
    }
    return emptyForm();
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.client_name?.trim()) e.client_name = 'Client name is required';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); setActiveTab(0); return; }
    setSaving(true);
    try {
      const payload = {};
      ALL_FIELDS.forEach(({ key }) => {
        if (form[key] !== null && form[key] !== undefined) {
          payload[key] = form[key];
        }
      });
      if (!isEdit) {
        payload['source'] = sourceOverride || 'add customer';
      }
      const url = isEdit ? `${TABLE_URL}?id=${record.id}` : TABLE_URL;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      onSaved(isEdit ? 'Record updated successfully' : 'Record created successfully');
      onClose();
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const { key, label, type = 'text', options, required } = field;
    const val = form[key] ?? '';
    const hasError = !!errors[key];
    const base = `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors
      ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`;

    return (
      <div key={key}>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {type === 'textarea' ? (
          <textarea rows={2} value={val} onChange={e => handleChange(key, e.target.value)}
            className={base + ' resize-none'} placeholder={label} />
        ) : type === 'select' ? (
          <select value={val} onChange={e => handleChange(key, e.target.value)} className={base}>
            <option value="">Select…</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input type={type} value={val} onChange={e => handleChange(key, e.target.value)}
            className={base} placeholder={label} />
        )}
        {hasError && <p className="mt-0.5 text-xs text-red-500">{errors[key]}</p>}
      </div>
    );
  };

  const currentSection = SECTIONS[activeTab];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">
            {isEdit ? `Edit Record — #${record.application_id || record.id}` : 'Add New Record'}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b border-gray-100 px-6 gap-1 overflow-x-auto">
          {SECTIONS.map((s, i) => (
            <button key={s.id} onClick={() => setActiveTab(i)}
              className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors
                ${activeTab === i
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {errors._global && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={15} className="shrink-0" />
              {errors._global}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {currentSection.fields.map(renderField)}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{activeTab + 1} / {SECTIONS.length}</span>
            <span className="font-medium text-gray-600">{currentSection.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setActiveTab(prev => Math.max(0, prev - 1))} disabled={activeTab === 0}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center gap-1">
              <ChevronLeft size={13} /> Prev
            </button>
            {activeTab < SECTIONS.length - 1 && (
              <button onClick={() => setActiveTab(prev => prev + 1)}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 flex items-center gap-1">
                Next <ChevronRight size={13} />
              </button>
            )}
            <button onClick={handleSubmit} disabled={saving}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 rounded-lg transition-all flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Record'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
